/**
 * EquipmentView component.
 * @module components/View/EquipmentView
 */
import { useEffect } from 'react';
import { BodyClass, hasBlocksData } from '@plone/volto/helpers';
import { Grid } from 'semantic-ui-react';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { UniversalLink, Image } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';
import { searchContent } from '@plone/volto/actions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEmpty, map } from 'lodash';
import { getSocialNetworkIcon } from '../../helpers'
import { Attachments, Map, RelatedEquipmentNews, RelatedEquipmentEvents } from '../';


const EquipmentView = (props) => {
  const dispatch = useDispatch();
  const { content } = props;

  const equipmentsLink = content.parent?.['@id'] ? flattenToAppURL(content.parent['@id']) : '';

  const getPosition = function (geolocation) {
    if (!geolocation) {
      return null
    }
    return geolocation.split(';').map(parseFloat);
  }
  const geolocation = getPosition(content.geolocation)

  const minisites = useSelector((state) => state.search.subrequests.minisites?.items);

  useEffect(() => {
    if (!content.related_minisites) return

    const uids = content.related_minisites.map((item) => item.token);
    const contentFilter = {
      portal_type: ['Minisite'],
      UID: uids,
    };
    dispatch(searchContent('/', contentFilter, 'minisites'));
  }, [dispatch, content]);

  const getSubjectLink = function (subject) {
    const subjectLink = `${equipmentsLink}?category=${subject}`;
    return subjectLink
  }

  return (
    <>
      <BodyClass className='equipment-view smallHeader' />
      <div className='equipmentItem'>
        <Grid className='mt-4'>
          <Grid.Row columns={12} className='align-items-start'>
            {content.preview_image && (
              <Grid.Column mobile={12} tablet={12} computer={7} widescreen={8} largeScreen={8}>
                <figure className='equipmentItem__figure'>

                  <Image className="equipmentItem__figure__img d-block"
                    alt=""
                    item={content}
                    imageField="preview_image"
                    responsive={true}
                    sizes="(max-width: 991px) 100vw, 25vw"
                  />
                  {content.preview_caption && (
                    <figcaption className='equipmentItem__figure__caption'>{content.preview_caption}</figcaption>
                  )}

                </figure>
                {/* Volto Blocks */}
                {hasBlocksData(content) ? (
                  <div className="userBlocks">
                    <RenderBlocks {...props} />
                  </div>
                ) : null}
              </Grid.Column>
            )}
            <Grid.Column className='flex-grow-1' mobile={12} tablet={12} computer={5} widescreen={4} largeScreen={4}>
              <dl className="equipmentItem__details mb-0 mt-5 mt-lg-0">
                {content.equipment_types && (
                  <div className="equipmentItem__concept">
                    <dt className="equipmentItem__concept__name"><FormattedMessage id="Type" defaultMessage="Type" /></dt>
                    <dd className="equipmentItem__concept__desc ms-0">
                      <ul className="list-unstyled my-0 d-flex gap-2 flex-wrap">
                        {map(content.equipment_types, (item) => (
                          <li key={item.token} >
                            <UniversalLink href={getSubjectLink(item.token)} className='subjectLabel'>{item?.title}</UniversalLink>
                          </li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                )}
                {!isEmpty(minisites) && (
                  <div className="equipmentItem__concept">
                    <dt className="equipmentItem__concept__name"><FormattedMessage id="Related minisites" defaultMessage="Related minisites" /></dt>
                    <dd className="equipmentItem__concept__desc ms-0">
                      <ul>
                        {map(minisites, (item) => (
                          <li key={item['@id']}>
                            <UniversalLink href={flattenToAppURL(item['@id'])}>{item.title}</UniversalLink>
                          </li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                )}
                {content?.location &&
                  <div className="equipmentItem__concept">
                    <dt className="equipmentItem__concept__name"><FormattedMessage id="Address" defaultMessage="Address" /></dt>
                    <dd className="equipmentItem__concept__desc ms-0">{content.location}</dd>
                  </div>
                }
                {content?.telephone &&
                  <div className="equipmentItem__concept">
                    <dt className="equipmentItem__concept__name"><FormattedMessage id="Phone" defaultMessage="Phone" /></dt>
                    <dd className="equipmentItem__concept__desc ms-0"><a href={"tel:+34" + content.telephone.replace(' ', '')}>{content.telephone}</a></dd>
                  </div>
                }
                {content?.email &&
                  <div className="equipmentItem__concept">
                    <dt className="equipmentItem__concept__name"><FormattedMessage id="Email" defaultMessage="Email" /></dt>
                    <dd className="equipmentItem__concept__desc ms-0"><a href={"mailto:" + content.email} >{content.email}</a></dd>
                  </div>
                }

                {content?.web &&
                  <div className="equipmentItem__concept">
                    <dt className="equipmentItem__concept__name"><FormattedMessage id="Website" defaultMessage="Website" /></dt>
                    <dd className="equipmentItem__concept__desc ms-0"><a href={content.web}>{content.web}</a></dd>
                  </div>
                }

                {!isEmpty(content.social_networks) && (

                  <div className="equipmentItem__concept">
                    <dt className="equipmentItem__concept__name"><FormattedMessage id="Networks" defaultMessage="Networks" /></dt>
                    <dd className="equipmentItem__concept__desc ms-0">
                      <ul className="list-unstyled my-0 equipmentItem__xxss">
                        {map(content.social_networks?.items, (social_network, index) => (
                          <li key={index} >
                            <a className="equipmentItem__xxss__link" href={social_network.link}>
                              <FontAwesomeIcon icon={getSocialNetworkIcon(social_network.social_network)} />
                              <span className="visually-hidden">{social_network.social_network}</span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                )}

                {(content.geolocation) && (
                  <div className="equipmentItem__concept equipmentItem__details__item--map" >
                    <dt className="equipmentItem__concept__name"><FormattedMessage id="Equipment map" defaultMessage="Equipment map" /></dt>
                    <dd className="equipmentItem__concept__desc ms-0">
                      <div className={'equipmentItem__details__map' + (!content.preview_image ? ' noImage' : ' ')}>
                        {geolocation && (
                          <Map marker={geolocation} center={geolocation} />
                        )}
                      </div>
                    </dd>
                  </div>
                )}
              </dl>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Attachments {...content} />
        <RelatedEquipmentEvents {...content} />
        <RelatedEquipmentNews {...content} />
      </div>
    </>
  );
};

export default EquipmentView;